import { Process_dateToISO } from "../utils/processors";

/**
 * Time period filter model
 * ---
 * Describes the selectable period types for time based listing filters
 * (createdOn / startedAt / endedAt) and the translation of a selected period
 * into backend filter conditions.
 *
 * The backend ({@code cz.green24.spring.webutils.filters.Filter}) resolves a set
 * of datetime placeholders for date fields, so preset periods are expressed with
 * those placeholders. There is no placeholder for a custom range, so it is sent
 * as two explicit conditions on the field ({@code field >= from} and
 * {@code field <= to}) using ISO-8601 datetime values.
 */

export const E_TimePeriod = {
	TODAY: "TODAY",
	YESTERDAY: "YESTERDAY",
	THIS_WEEK: "THIS_WEEK",
	LAST_WEEK: "LAST_WEEK",
	THIS_MONTH: "THIS_MONTH",
	LAST_MONTH: "LAST_MONTH",
	CUSTOM: "CUSTOM",
};

const PERIOD_QUERY_SEPARATOR = "~"; //URL safe (not encoded, does not clash with the filter/kv separators)

/**
 * Preset period -> [startPlaceholder, endPlaceholder]
 * Placeholder names must match Filter.DateTimePlaceHolder on the backend.
 */
const PERIOD_PLACEHOLDERS = {
	[E_TimePeriod.TODAY]: ["DAY_START", "DAY_END"],
	[E_TimePeriod.YESTERDAY]: ["YESTERDAY_START", "YESTERDAY_END"],
	[E_TimePeriod.THIS_WEEK]: ["WEEK_START", "WEEK_END"],
	[E_TimePeriod.LAST_WEEK]: ["LAST_WEEK_START", "LAST_WEEK_END"],
	[E_TimePeriod.THIS_MONTH]: ["MONTH_START", "MONTH_END"],
	[E_TimePeriod.LAST_MONTH]: ["LAST_MONTH_START", "LAST_MONTH_END"],
};

const pad = num => `${num}`.padStart(2, "0");

/**
 * Local calendar date (YYYY-MM-DD) -> ISO-8601 datetime for the day boundary in
 * the user's local timezone (start of day 00:00:00, or end of day 23:59:59).
 * @param {String} dateString
 * @param {Boolean} endOfDay false => 00:00:00, true => 23:59:59
 * @returns {String|null}
 */
const dateStringToBoundaryISO = (dateString, endOfDay) => {
	let [year, month, day] = `${dateString || ''}`.split("-").map(part => parseInt(part, 10));
	if (!year || !month || !day) {
		return null;
	}
	return Process_dateToISO(new Date(year, month - 1, day), false, endOfDay);
};

/**
 * Builds the (opaque) filter value token stored in the query string.
 * @param {String} period E_TimePeriod
 * @param {String} from local calendar date (YYYY-MM-DD)
 * @param {String} to local calendar date (YYYY-MM-DD)
 * @returns {String}
 */
export const buildPeriodToken = (period, from, to) => {
	if (!period) {
		return '';
	}
	if (period !== E_TimePeriod.CUSTOM) {
		return period;
	}
	return [E_TimePeriod.CUSTOM, from || '', to || ''].join(PERIOD_QUERY_SEPARATOR);
};

/**
 * Parses a period token back into its parts.
 * @param {String} value
 * @returns {{period: (String|null), from: (String|null), to: (String|null)}}
 */
export const parsePeriodValue = (value) => {
	let [period, from, to] = `${value || ''}`.split(PERIOD_QUERY_SEPARATOR);
	return {
		period: period || null,
		from: from || null,
		to: to || null,
	};
};

/**
 * Translates a period token into backend filter conditions for the given field.
 * A period always maps to a lower and/or upper bound on the same field, joined
 * with AND.
 * @param {String} value period token
 * @param {String} field entity field path (e.g. "createdOn")
 * @returns {Array<{field: String, operator: String, value: String, join: String}>}
 */
export const buildPeriodQueryFilters = (value, field) => {
	if (!value || !field) {
		return [];
	}

	let {period, from, to} = parsePeriodValue(value);

	if (period !== E_TimePeriod.CUSTOM) {
		let bounds = PERIOD_PLACEHOLDERS[period];
		if (!bounds) {
			return [];
		}
		return [
			{field, operator: ">=", value: bounds[0], join: "AND"},
			{field, operator: "<=", value: bounds[1], join: "AND"},
		];
	}

	let filters = [];
	let fromISO = dateStringToBoundaryISO(from, false);
	let toISO = dateStringToBoundaryISO(to, true);
	if (fromISO) {
		filters.push({field, operator: ">=", value: fromISO, join: "AND"});
	}
	if (toISO) {
		filters.push({field, operator: "<=", value: toISO, join: "AND"});
	}
	return filters;
};

/**
 * Converts a Date / ISO string emitted by the datepicker into a local calendar
 * date string (YYYY-MM-DD), using the local day the user actually selected.
 * @param {String|Date} value
 * @returns {String|null}
 */
export const toCalendarDateString = (value) => {
	if (!value) {
		return null;
	}
	let date = new Date(value);
	if (isNaN(date.getTime())) {
		return null;
	}
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
